import billingService from "../services/billing.service";
import { readPagination } from "../utils/api";

const readMessage = (resp, fallback) =>
  resp?.data?.data?.error ||
  resp?.data?.error ||
  resp?.data?.data?.message ||
  resp?.data?.message ||
  fallback;

const ok = (resp) => resp?.status === 200 || resp?.status === 201;

// The API names the case in `code` so the UI can branch on it instead of
// reading the sentence in `error`, which is written for the customer.
export const readCode = (resp) => resp?.data?.code || resp?.data?.data?.code || "";

// Which card popup to open. The endpoint names its own provider, so this is
// read rather than decided here - but it is read loosely: "Paddle",
// "paddle_billing" and a stray space all mean Paddle, and matching them
// exactly would silently drop the customer into the Helcim popup instead.
const readProvider = (checkout) => {
  const named = String(checkout?.provider || "").trim().toLowerCase();

  if (named.startsWith("paddle")) return "paddle";
  if (named) return named;

  // Unnamed: a Paddle transaction is still unmistakably Paddle, and
  // everything that came before Paddle was Helcim.
  return readTransactionId(checkout) ? "paddle" : "helcim";
};

// Paddle's id for the transaction to open. Spellings vary by serializer, and
// the popup cannot open without it.
const readTransactionId = (checkout) =>
  checkout?.transactionId ||
  checkout?.transaction_id ||
  checkout?.paddleTransactionId ||
  null;

/**
 * The checkout a payment endpoint hands back - the same shape from
 * POST /billing/checkout, POST /billing/retry-payment and the guest
 * registration.
 *
 * Which provider charges the card is the endpoint's to say: Paddle answers
 * with `provider: "paddle"` and a transaction id, Helcim with a
 * `checkoutToken` that opens its popup and completes the payment, good for
 * 60 minutes. Anything with neither is not a checkout.
 */
export const readCheckout = (body) => {
  const checkout = body?.checkout || body || {};
  const provider = readProvider(checkout);
  const transactionId = readTransactionId(checkout);

  if (!checkout.checkoutToken && !(provider === "paddle" && transactionId)) {
    return null;
  }

  return {
    checkoutToken: checkout.checkoutToken || null,
    provider,
    transactionId,
    // What the card is charged now.
    amount: typeof checkout.amount === "number" ? checkout.amount : null,
    // "new", "upgrade" or "retry".
    kind: checkout.kind || "",
    plan: checkout.plan || "",
    interval: checkout.interval || "",
    message: checkout.message || "",
  };
};

/** A paused account answers 402 on everything outside billing and account. */
export const PAYMENT_REQUIRED = "payment_required";
export const SUBSCRIPTION_ENDED = "subscription_ended";
export const RETRY_LIMIT = "retry_limit";

// The API uses -1 for "no cap".
export const UNLIMITED = -1;

export const isUnlimited = (value) => value === UNLIMITED;

/** -1 reads as Unlimited, a number gets thousands separators. */
export const formatLimit = (value) => {
  if (isUnlimited(value)) return "Unlimited";
  if (typeof value !== "number") return "—";
  return value.toLocaleString();
};

export const planLabel = (plan) =>
  plan ? `${String(plan).charAt(0).toUpperCase()}${String(plan).slice(1)}` : "";

/**
 * GET /billing/subscription — the authoritative record for the current plan.
 * /auth/me carries a subscription too, but only some of the limits; this is
 * the endpoint that answers with plan, maxAssistants and
 * monthlyChatLimitPerAssistant directly.
 */
export const getSubscription = () => async () => {
  const resp = await billingService.getSubscription();
  const body = resp?.data?.data || resp?.data;

  if (ok(resp) && body) {
    return { success: true, response: resp, subscription: body };
  }

  return {
    success: false,
    response: resp,
    subscription: null,
    message: readMessage(resp, "We couldn't load your subscription."),
  };
};

export const listPlans = () => async () => {
  const resp = await billingService.getPlans();
  const body = resp?.data?.data || resp?.data;
  const plans = Array.isArray(body) ? body : body?.plans;

  if (ok(resp) && Array.isArray(plans)) {
    return { success: true, response: resp, plans };
  }

  return {
    success: false,
    response: resp,
    plans: [],
    message: readMessage(resp, "We couldn't load the available plans."),
  };
};

/**
 * GET /billing/checkout/preview - the prorated cost of a change, before any
 * money moves. Shown to the customer so the amount is never a surprise.
 *
 * @param {string} plan
 * @param {"monthly"|"annual"} interval
 */
export const previewCheckout =
  (plan, interval = "monthly", { guest = false } = {}) =>
  async () => {
    if (!plan) return { success: false, message: "No plan selected." };

    // guest=1 is the public checkout page: there is no account yet, so the
    // quote is the plan's own price rather than a change to an existing one.
    // Without it the endpoint answers "Not signed in" and the page has no
    // figures to show.
    const resp = await billingService.getCheckoutPreview(
      guest ? { plan, interval, guest: 1 } : { plan, interval },
    );
    const body = resp?.data?.data || resp?.data;

    if (ok(resp) && body) {
      return { success: true, response: resp, preview: body };
    }

    return {
      success: false,
      response: resp,
      code: readCode(resp),
      message: readMessage(resp, "We couldn't work out what this would cost."),
    };
  };

/**
 * Starts an upgrade. The API is expected to return a hosted checkout URL; the
 * caller sends the browser there.
 *
 * @param {string} plan
 * @param {"monthly"|"annual"} interval which plan id to charge - every
 *   tier carries both helcimPlanIdMonthly and helcimPlanIdAnnual
 */
export const startCheckout = (plan, interval = "monthly") => async () => {
  if (!plan) return { success: false, message: "No plan selected." };

  const resp = await billingService.createCheckout({ plan, interval });
  const body = resp?.data?.data || resp?.data || {};
  const checkout = readCheckout(body);

  if (ok(resp) && checkout) {
    return { success: true, response: resp, checkout };
  }

  // A 200 with no checkout means the change was applied server-side instead -
  // nothing to charge, so nothing to open.
  if (ok(resp)) {
    return { success: true, response: resp, checkout: null };
  }

  return {
    success: false,
    response: resp,
    code: readCode(resp),
    // 400 is a change the preview already refused (locked downgrade, blocked,
    // a failed payment); 403 is the assistant allowance. Both answer with the
    // sentence the preview showed, so it is passed through as sent.
    message: readMessage(resp, "We couldn't start that plan change."),
  };
};

/**
 * POST /billing/retry-payment - charges a failed renewal again, on a card the
 * customer enters in the popup. Answers with the same checkout shape as a
 * purchase, with `kind: "retry"`. The cycle restarts the day it clears, and
 * access comes back with it.
 *
 * 400 when nothing is past due, 429 (code `retry_limit`) after three tries
 * in a day.
 */
export const retryPayment = () => async () => {
  const resp = await billingService.retryPayment();
  const body = resp?.data?.data || resp?.data || {};

  if (ok(resp)) {
    return {
      success: true,
      response: resp,
      checkout: readCheckout(body),
      message: body.message || "",
    };
  }

  const code = readCode(resp);

  return {
    success: false,
    response: resp,
    code,
    message: readMessage(
      resp,
      code === RETRY_LIMIT
        ? "You've tried a few times today. Please try again tomorrow."
        : "We couldn't start that payment.",
    ),
  };
};

/**
 * POST /billing/checkout/complete - hands the popup's result to the backend,
 * which verifies it and activates the plan. No session needed: the checkout
 * token is the proof, which is what lets the public checkout page finish a
 * payment for an account that cannot sign in yet.
 *
 * Answers "completed" (the plan is live) or "processing" (give it a few
 * seconds and read the subscription again). 400 means the payment could not
 * be confirmed; 404 an unknown token. Calling it twice for one payment is
 * safe.
 *
 * @param {string} checkoutToken
 * @param {object} data the popup's `data`, passed through untouched
 * @param {string} hash the popup's `hash`, likewise
 */
export const completeCheckout = (checkoutToken, data, hash) => async () => {
  if (!checkoutToken) {
    return { success: false, message: "That payment has no checkout to finish." };
  }

  const resp = await billingService.completeCheckout({
    checkoutToken,
    data,
    hash,
  });
  const body = resp?.data?.data || resp?.data || {};

  if (ok(resp)) {
    return {
      success: true,
      response: resp,
      // "completed" or "processing".
      status: body.status || "",
      plan: body.plan || "",
      interval: body.interval || "",
      message: body.message || "",
    };
  }

  return {
    success: false,
    response: resp,
    code: readCode(resp),
    message: readMessage(
      resp,
      resp?.status === 404
        ? "That payment could not be found. If you were charged, contact support."
        : "We couldn't confirm that payment.",
    ),
  };
};

/**
 * GET /billing/invoices - one row per completed charge, newest first.
 * The collection may arrive bare or wrapped, so it is read the same way as
 * every other list in the app.
 */
export const listInvoices = (params) => async () => {
  const resp = await billingService.getInvoices(params);
  const body = resp?.data?.data || resp?.data;
  const invoices = Array.isArray(body) ? body : body?.invoices || body?.data;

  if (ok(resp) && Array.isArray(invoices)) {
    return {
      success: true,
      response: resp,
      invoices,
      pagination: readPagination(resp, params?.limit),
    };
  }

  return {
    success: false,
    response: resp,
    invoices: [],
    message: readMessage(resp, "We couldn't load your payment history."),
  };
};

// content-disposition: attachment; filename="hostor-invoice-<id>.pdf"
const filenameFrom = (disposition, fallback) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  return match ? decodeURIComponent(match[1]) : fallback;
};

/**
 * GET /billing/invoices/:id/pdf, saved to the visitor's downloads.
 *
 * The browser cannot be sent to the URL directly - it needs the session
 * token - so the body is fetched and handed to an object URL instead.
 */
export const downloadInvoicePdf = (id) => async () => {
  if (!id) return { success: false, message: "No invoice to download." };

  const resp = await billingService.getInvoicePdf(id);

  if (!ok(resp) || !resp.data) {
    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't download that invoice."),
    };
  }

  const blob =
    resp.data instanceof Blob
      ? resp.data
      : new Blob([resp.data], { type: "application/pdf" });

  const name = filenameFrom(
    resp.headers?.["content-disposition"],
    `hostor-invoice-${id}.pdf`,
  );

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Released on the next tick: revoking synchronously can cancel the download
  // in some browsers before it has read the blob.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);

  return { success: true, response: resp, filename: name };
};

/**
 * POST /billing/cancel - turns auto-renewal off. It does not end the plan:
 * everything keeps working until the end of the period already paid for, and
 * only then does the account pause (status "ended"). A subscription that is
 * already past due ends immediately instead, since nothing is paid for.
 *
 * Cannot be undone - coming back means buying again, which the preview
 * answers as "resubscribe".
 */
export const cancelSubscription = () => async () => {
  const resp = await billingService.cancelSubscription();

  if (ok(resp) || resp?.status === 204) {
    return { success: true, response: resp };
  }

  return {
    success: false,
    response: resp,
    message: readMessage(
      resp,
      resp?.status === 400
        ? "There is no paid subscription to cancel."
        : "We couldn't cancel your subscription.",
    ),
  };
};
